import {SortOrderEnum} from './Common.types'

/** Microsoft consent type: AllPrincipals (all users) or Principal (specific user) */
export type MicrosoftConsentType = 'AllPrincipals' | 'Principal'

/** How consent was granted */
export enum SSPMAppConsentMethodEnum {
  Admin = 'admin',
  User = 'user',
  Combined = 'combined', // both admin and user consent
}

/** Dope categorization of app access scope */
export enum SSPMAppCategoryEnum {
  Global = 'global', // organization-wide
  Scoped = 'scoped', // limited access
  Login = 'login', // identity-only
}

/** Risk level assessment for the app */
export enum SSPMAppRiskLevelEnum {
  Low = 'low',
  Medium = 'medium',
  High = 'high',
  Critical = 'critical',
}

/** App level permissions */
export interface AppRole {
  /** The MS unique id for this app role */
  msAppRoleId: string

  /** The name of the resource the permission is against, e.g. Microsoft Graph */
  resourceName: string

  /** The permission granted, e.g. User.Read.All */
  permission: string

  /** The description of the granted permission, e.g. "Read all users' full profiles" */
  description: string
}

/** Application User data */
export interface SSPMUserData {
  /** The user id */
  msUserId: string

  /** The user display name */
  displayName: string

  /** The user email address */
  email: string
}

/** Delegated grant permissions */
export interface DelegatedGrant {
  /** The name of the resource the permission is against, e.g. Microsoft Graph */
  resourceName: string

  /** Indicates if the consent is for all or a specific principal */
  consentType: MicrosoftConsentType

  /** The user data for the principal, optional if the consentType is AllPrincipals */
  userData?: SSPMUserData

  /** The list of scopes granted, e.g. "openid profile User.Read offline_access Files.ReadWrite" */
  scopes: string
}

/** General information about the application */
export interface GeneralAppInfo {
  logoUrl?: string
  homepageUrl?: string
  termsOfServiceUrl?: string
  privacyStatementUrl?: string
}

/** Information about the organization that owns the app */
export interface OrganizationData {
  /** The organization's MS tenant id */
  msTenantId: string
}

/** Dope's risk assessment for the application */
export interface SSPMAppRiskAssessment {
  /** Overall risk level categorization */
  riskLevel: SSPMAppRiskLevelEnum

  /** Calculated risk score (product of impact and likelihood) - Range: 1-25 */
  riskScore: number

  /** Impact score: potential damage if compromised - Range: 1-5 */
  impactScore: number

  /** Likelihood score: probability of being exploited - Range: 1-5 */
  likelihoodScore: number

  /** List of specific risk factors identified for this app (returned by Microsoft) */
  riskFactors: string[]
}

export interface MicrosoftAppListItem {
  /** MS unique id for this app instance */
  msObjectId: string

  /** MS unique id for this app */
  msAppId: string

  /** Display name of the application */
  displayName: string

  /** Publisher name - presence indicates this is a verified app */
  publisherName?: string

  /** The date and time the app was created in Microsoft for the customer (ISO 8601 format) */
  createdDateTime?: string

  /** The date and time the app was last synchronised with SSPM (ISO 8601 format) */
  lastSyncDateTime: string

  /** The user who created (or first authorized) the app */
  creator?: SSPMUserData

  /** Information about the app */
  appInfo: GeneralAppInfo

  /** Information about the MS tenant that originated the app */
  appOwnerOrganization: OrganizationData

  /** The enablement status of the app */
  appEnabled: boolean

  /** Indicates if the app has been disabled by Microsoft due to DisabledDueToViolationOfServicesAgreement */
  disabledByMicrosoft: boolean

  /** Owners assigned to the app */
  owners: SSPMUserData[]

  /** Dope overall categorization of the app */
  category: SSPMAppCategoryEnum

  /** Dope categorization of the consent method used for the app */
  consentMethod: SSPMAppConsentMethodEnum

  /** Dope risk assessment details for the app */
  riskAssessment: SSPMAppRiskAssessment
}

/** Applications list type */
export type SSPMApplicationsListType = MicrosoftAppListItem[]

/** Response containing Microsoft/Google third-party apps with pagination */
export interface SSPMApplicationsAPIResponse {
  /** Array of Microsoft third-party applications */
  apps: SSPMApplicationsListType

  /** Cursor-based pagination information */
  pageInfo: SSPMApplicationsPageInfo
}

export interface SSPMApplicationsAPIResponseData {
  data: SSPMApplicationsAPIResponse
}

/** Response containing app details including roles and delegated grants with pagination */
export interface SSPMAppDetailsResponse {
  /** Microsoft unique id for this app */
  msObjectId: string

  /** Display name of the application */
  appDisplayName: string

  /** App level permissions assigned to the app - can be empty and not present in subsequent pages */
  appRoles?: AppRole[]

  /** Array of delegated permission grants - can be empty */
  delegatedGrants: DelegatedGrant[]

  /** Cursor-based pagination information - needed for additional pages of delegated grants */
  pageInfo: SSPMApplicationsPageInfo
}

export interface SSPMAppDetailsResponseData {
  data: SSPMAppDetailsResponse
}

export interface SSPMAppDetailsAPIParams {
  /** Cursor for the next page of delegated grants */
  after?: string
}

/** Cursor-based pagination information */
export interface SSPMApplicationsPageInfo {
  /** Cursor for the next page */
  nextCursor?: string

  /** Whether there are more results available */
  hasNextPage: boolean
}

/** Parameters for getting SSPM applications */
export interface SSPMApplicationsAPIParams {
  /**
   * Sort order for results. Format: "field:direction" or "field" (defaults to asc).
   * Allowed fields: createdDateTime, riskLevel, category, consentMethod, displayName, publisherName, creatorEmail.
   * Direction: asc or desc.
   */
  sort?: string

  /** Cursor for the next page */
  after?: string

  /** Filter by risk level (comma-separated values) */
  riskLevel?: string

  /** Filter by consent method (comma-separated values) */
  consentMethod?: string

  /** Filter by category (comma-separated values) */
  category?: string

  /**
   * General fuzzy search by application display name, publisher name or creator's email address (case-insensitive substring match).
   * Only one of the search query-params, `search`, `displayName`, `publisherName` or `creatorEmail` can be provided at a time.
   */
  search?: string

  /** Exact search by creator email address (case-insensitive substring match) */
  creatorEmail?: string
}

export type AppliedFilters = {
  category: string[]
  riskLevel: string[]
  consent: string[]
  search: string
  creatorEmail: string
  sort: string
  direction?: SortOrderEnum
}
